import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../services/adminApi';
import './AdminServerHealth.css';

function AdminServerHealth() {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    const loadHealth = useCallback(async () => {
        try {
            const res = await adminApi.getHealth();
            setHealth(res.data);
            setLastUpdate(new Date());
            setError(null);
        } catch (err) {
            setError('Erreur lors du chargement des donnees serveur');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHealth();

        // Rafraichir toutes les 30 secondes
        const interval = setInterval(loadHealth, 30000);
        return () => clearInterval(interval);
    }, [loadHealth]);

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatUptime = (seconds) => {
        if (!seconds) return 'N/A';
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) {
            return `${days}j ${hours}h`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'healthy': return 'healthy';
            case 'warning': return 'warning';
            case 'critical': return 'critical';
            default: return '';
        }
    };

    const getProgressColor = (percent) => {
        if (percent >= 90) return '#dc2626';
        if (percent >= 70) return '#d97706';
        return '#6366f1';
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="admin-loading-inline">Chargement...</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-server-health">
                <div className="admin-page-header with-actions">
                    <div>
                        <h1>Sante du serveur</h1>
                        <p>Monitoring en temps reel de l'infrastructure</p>
                    </div>
                    <div className="header-actions">
                        <span className="last-update">
                            Mis a jour: {lastUpdate?.toLocaleTimeString('fr-FR')}
                        </span>
                        <button
                            className="admin-btn admin-btn-secondary"
                            onClick={loadHealth}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                            </svg>
                            Actualiser
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="admin-alert error">{error}</div>
                )}

                {/* Global Status */}
                <div className="status-banner">
                    <div className={`status-indicator ${getStatusClass(health?.status)}`}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {health?.status === 'healthy' ? (
                                <path d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" />
                            ) : (
                                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            )}
                        </svg>
                        <span className="status-text">
                            {health?.status === 'healthy' ? 'Tous les systemes sont operationnels' :
                             health?.status === 'warning' ? 'Attention: certains indicateurs necessitent une surveillance' :
                             'Critique: action requise'}
                        </span>
                    </div>
                </div>

                {/* Warnings */}
                {health?.warnings?.length > 0 && (
                    <div className="warnings-section">
                        {health.warnings.map((warning, index) => (
                            <div key={index} className="warning-item">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                {warning}
                            </div>
                        ))}
                    </div>
                )}

                {/* Metrics Grid */}
                <div className="metrics-grid">
                    {/* PHP Info */}
                    <div className="admin-card metric-card">
                        <div className="metric-header">
                            <div className="metric-icon php">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <ellipse cx="12" cy="12" rx="10" ry="6" />
                                </svg>
                            </div>
                            <h3>PHP</h3>
                        </div>
                        <div className="metric-content">
                            <div className="metric-main">
                                <span className="metric-value">{health?.php?.version || 'N/A'}</span>
                                <span className="metric-label">Version</span>
                            </div>
                            <div className="metric-details">
                                <div className="detail-item">
                                    <span className="detail-label">Memoire max</span>
                                    <span className="detail-value">{health?.php?.memory_limit || 'N/A'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Upload max</span>
                                    <span className="detail-value">{health?.php?.upload_max_filesize || 'N/A'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Post max</span>
                                    <span className="detail-value">{health?.php?.post_max_size || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MySQL Info */}
                    <div className="admin-card metric-card">
                        <div className="metric-header">
                            <div className="metric-icon mysql">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                                </svg>
                            </div>
                            <h3>MySQL</h3>
                        </div>
                        <div className="metric-content">
                            <div className="metric-main">
                                <span className="metric-value">{health?.mysql?.version?.split('-')[0] || 'N/A'}</span>
                                <span className="metric-label">Version</span>
                            </div>
                            <div className="metric-details">
                                <div className="detail-item">
                                    <span className="detail-label">Connexions</span>
                                    <span className="detail-value">{health?.mysql?.connections || 0}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Uptime</span>
                                    <span className="detail-value">{formatUptime(health?.mysql?.uptime_seconds)}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Slow queries</span>
                                    <span className="detail-value">{health?.mysql?.slow_queries || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Memory */}
                    <div className="admin-card metric-card">
                        <div className="metric-header">
                            <div className="metric-icon memory">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="4" y="4" width="16" height="16" rx="2" />
                                    <rect x="9" y="9" width="6" height="6" />
                                    <line x1="9" y1="1" x2="9" y2="4" />
                                    <line x1="15" y1="1" x2="15" y2="4" />
                                    <line x1="9" y1="20" x2="9" y2="23" />
                                    <line x1="15" y1="20" x2="15" y2="23" />
                                </svg>
                            </div>
                            <h3>Memoire</h3>
                        </div>
                        <div className="metric-content">
                            <div className="metric-main">
                                <span className="metric-value">{formatBytes(health?.memory?.usage || 0)}</span>
                                <span className="metric-label">Utilisation actuelle</span>
                            </div>
                            <div className="metric-bar-container">
                                <div className="metric-bar">
                                    <div
                                        className="metric-bar-fill"
                                        style={{
                                            width: `${((health?.memory?.usage || 0) / (health?.memory?.limit || 1)) * 100}%`,
                                            backgroundColor: getProgressColor(((health?.memory?.usage || 0) / (health?.memory?.limit || 1)) * 100)
                                        }}
                                    />
                                </div>
                                <span className="metric-bar-label">
                                    {formatBytes(health?.memory?.usage || 0)} / {formatBytes(health?.memory?.limit || 0)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Disk */}
                    <div className="admin-card metric-card">
                        <div className="metric-header">
                            <div className="metric-icon disk">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                                    <polyline points="3.27,6.96 12,12.01 20.73,6.96" />
                                    <line x1="12" y1="22.08" x2="12" y2="12" />
                                </svg>
                            </div>
                            <h3>Disque</h3>
                        </div>
                        <div className="metric-content">
                            <div className="metric-main">
                                <span className="metric-value">{health?.disk?.usage_percent?.toFixed(1) || 0}%</span>
                                <span className="metric-label">Utilisation</span>
                            </div>
                            <div className="metric-bar-container">
                                <div className="metric-bar">
                                    <div
                                        className="metric-bar-fill"
                                        style={{
                                            width: `${health?.disk?.usage_percent || 0}%`,
                                            backgroundColor: getProgressColor(health?.disk?.usage_percent || 0)
                                        }}
                                    />
                                </div>
                                <span className="metric-bar-label">
                                    {formatBytes(health?.disk?.free || 0)} disponibles
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Load Average */}
                    <div className="admin-card metric-card">
                        <div className="metric-header">
                            <div className="metric-icon load">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="2" x2="12" y2="6" />
                                    <line x1="12" y1="18" x2="12" y2="22" />
                                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                                    <line x1="2" y1="12" x2="6" y2="12" />
                                    <line x1="18" y1="12" x2="22" y2="12" />
                                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                                    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                                </svg>
                            </div>
                            <h3>Charge systeme</h3>
                        </div>
                        <div className="metric-content">
                            <div className="load-values">
                                <div className="load-item">
                                    <span className="load-value">{health?.load_average?.['1min']?.toFixed(2) || 'N/A'}</span>
                                    <span className="load-label">1 min</span>
                                </div>
                                <div className="load-item">
                                    <span className="load-value">{health?.load_average?.['5min']?.toFixed(2) || 'N/A'}</span>
                                    <span className="load-label">5 min</span>
                                </div>
                                <div className="load-item">
                                    <span className="load-value">{health?.load_average?.['15min']?.toFixed(2) || 'N/A'}</span>
                                    <span className="load-label">15 min</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cron Jobs */}
                    <div className="admin-card metric-card">
                        <div className="metric-header">
                            <div className="metric-icon cron">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12,6 12,12 16,14" />
                                </svg>
                            </div>
                            <h3>Taches planifiees</h3>
                        </div>
                        <div className="metric-content">
                            <div className="cron-status">
                                <div className={`cron-indicator ${health?.cron?.last_run ? 'active' : 'inactive'}`}>
                                    {health?.cron?.last_run ? 'Operationnel' : 'Inactif'}
                                </div>
                                {health?.cron?.last_run && (
                                    <span className="cron-last-run">
                                        Derniere execution: {new Date(health.cron.last_run).toLocaleString('fr-FR')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminServerHealth;
